import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ExpiryDropdown from './ExpiryDropdown';
import ErrorLabel from './ErrorLabel';

const checkIsValid = v => {
    const now = new Date();
    return (
        Boolean(v.month && v.year) &&
        (v.year > now.getFullYear() ||
            (v.year === now.getFullYear() && v.month >= now.getMonth() + 1))
    );
};

const ExpiryInput = ({ readOnly, onChange, expiryAsText, forceDirty, expiryMonthYear }) => {
    const { t } = useTranslation();

    const [isValid, setValid] = React.useState(true);
    const [value, setValue] = React.useState({});
    const [textValue, setTextValue] = React.useState('');
    const [isDirty, setDirty] = React.useState(false);
    const [isFocused, setFocused] = React.useState(false);

    React.useEffect(() => {
        const valid = checkIsValid(value);

        if (isValid !== valid) {
            setValid(valid);
            onChange({
                value,
                isValid: valid
            });
        }
    }, [value, onChange, isValid]);

    React.useEffect(() => {
        if (expiryMonthYear) {
            setTextValue(expiryMonthYear.month + '/' + expiryMonthYear.year);
            setValue(expiryMonthYear);
        } else {
            setTextValue('');
            setValue({});
        }
    }, [expiryMonthYear]);

    const updateValueFromDropdown = React.useCallback(
        v => {
            setDirty(true);
            setValue(v);
        },
        [setDirty, setValue]
    );

    return (
        <div
            className={`wz-lyriapay-payline-formblock wz-lyriapay-payline-formblock-expiry${
                (isDirty || forceDirty) && !isValid
                    ? ' wz-lyriapay-payline-formblock-error'
                    : ''
            }`}
        >
            {expiryAsText ? (
                <>
                    <label htmlFor="exp-date">{t('expiryLabel')}</label>
                    <input
                        name="exp-date"
                        placeholder="MM/YY"
                        autoComplete="cc-exp"
                        required
                        value={textValue}
                        disabled={readOnly}
                        onBlur={() => setFocused(false)}
                        onFocus={() => setFocused(true)}
                        onChange={e => {
                            const exp = e.target.value.split('/');
                            setTextValue(e.target.value);
                            setDirty(true);

                            const parsedYear = parseInt(exp[1]);
                            let year;
                            if (parsedYear < 0) {
                                year = null;
                            } else if (parsedYear < 1000) {
                                year = parsedYear + 2000;
                            } else {
                                year = parsedYear;
                            }

                            const parsedMonth = parseInt(exp[0]);
                            let month;
                            if (parsedMonth < 0 || parsedMonth > 12) {
                                month = null;
                            } else {
                                month = parsedMonth;
                            }

                            setValue({ month, year });
                        }}
                        aria-invalid={
                            (isDirty || forceDirty) && !isValid && !isFocused
                        }
                        aria-required
                    />
                </>
            ) : (
                <ExpiryDropdown
                    onChange={updateValueFromDropdown}
                    onBlur={() => setFocused(false)}
                    onFocus={() => setFocused(true)}
                    expiryMonthYear={expiryMonthYear}
                    isReadOnly={readOnly}
                />
            )}
            <ErrorLabel
                content={
                    (isDirty || forceDirty) && !isFocused
                        ? value.month && value.year
                            ? isValid
                                ? null
                                : t('expiryInvalid')
                            : t('expiryEmpty')
                        : null
                }
            />
        </div>
    );
};

ExpiryInput.propTypes = {
    readOnly: PropTypes.bool.isRequired,
    expiryAsText: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    forceDirty: PropTypes.bool,
    expiryMonthYear: PropTypes.object
};

export default ExpiryInput;
